import { useState } from 'react';
import { MessageSquare, AlertCircle, Users, Clock, X } from 'lucide-react';
import useDeepCompareEffect from 'use-deep-compare-effect';
interface ErrorMessageConfig {
  message?: string;
  details?: string;
}

interface FlowErrorMessages {
  formClosed?: ErrorMessageConfig;
  registrationFull?: ErrorMessageConfig;
  notInPeriod?: ErrorMessageConfig;
  checkFailed?: ErrorMessageConfig;
}

interface ErrorMessageSettingsProps {
  errorMessages?: FlowErrorMessages;
  onErrorMessagesChange: (errorMessages: FlowErrorMessages) => void;
}

const defaultMessages = {
  formClosed: {
    message: "ฟอร์มนี้ปิดให้บริการแล้ว",
    details: "ไม่สามารถเข้าถึงฟอร์มได้ในขณะนี้"
  },
  registrationFull: {
    message: "ฟอร์มนี้มีผู้ลงทะเบียนเต็มแล้ว",
    details: "จำนวนผู้ลงทะเบียนปัจจุบัน: {current_register} คน, จำนวนที่รับสูงสุด: {maximum_register} คน"
  },
  notInPeriod: {
    message: "ฟอร์มนี้ยังไม่เปิดให้บริการ",
    details: "ฟอร์มจะเปิดให้บริการตั้งแต่ {start_date} ถึง {end_date}"
  },
  checkFailed: {
    message: "ไม่สามารถตรวจสอบการเข้าถึงได้",
    details: "กรุณาลองใหม่อีกครั้ง"
  }
};

const errorTypes = [
  {
    key: 'formClosed' as keyof FlowErrorMessages,
    label: 'ฟอร์มปิดให้บริการ',
    description: 'เมื่อฟอร์มหมดเวลาการให้บริการ',
    icon: X,
    color: 'text-red-600'
  },
  {
    key: 'registrationFull' as keyof FlowErrorMessages,
    label: 'ฟอร์มเต็มแล้ว',
    description: 'เมื่อจำนวนผู้ลงทะเบียนเต็ม',
    icon: Users,
    color: 'text-orange-600'
  },
  {
    key: 'notInPeriod' as keyof FlowErrorMessages,
    label: 'ยังไม่เปิดให้บริการ',
    description: 'เมื่อยังไม่ถึงเวลาที่กำหนด',
    icon: Clock,
    color: 'text-blue-600'
  },
  {
    key: 'checkFailed' as keyof FlowErrorMessages,
    label: 'ตรวจสอบล้มเหลว',
    description: 'เมื่อไม่สามารถตรวจสอบการเข้าถึงได้',
    icon: AlertCircle,
    color: 'text-gray-600'
  }
];

export function ErrorMessageSettings({ 
  errorMessages = {}, 
  onErrorMessagesChange 
}: ErrorMessageSettingsProps) {
  const [localErrorMessages, setLocalErrorMessages] = useState<FlowErrorMessages>(errorMessages);
  const [expandedType, setExpandedType] = useState<keyof FlowErrorMessages | null>(null);

  useDeepCompareEffect(() => {
    setLocalErrorMessages(errorMessages);
  }, [errorMessages]);

  const handleMessageChange = (type: keyof FlowErrorMessages, field: 'message' | 'details', value: string) => {
    const updated = {
      ...localErrorMessages,
      [type]: {
        ...localErrorMessages[type],
        [field]: value
      }
    };
    setLocalErrorMessages(updated);
    onErrorMessagesChange(updated);
  };

  const handleResetToDefault = (type: keyof FlowErrorMessages) => {
    const updated = {
      ...localErrorMessages,
      [type]: defaultMessages[type]
    };
    setLocalErrorMessages(updated);
    onErrorMessagesChange(updated);
  };

  const handleResetAll = () => {
    setLocalErrorMessages({});
    onErrorMessagesChange({});
  };

  const isCustomized = (type: keyof FlowErrorMessages) => {
    const current = localErrorMessages[type];
    const default_ = defaultMessages[type];
    return current && (
      current.message !== default_.message || 
      current.details !== default_.details
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Error Messages</h3>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleResetAll}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Reset All
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        ปรับแต่งข้อความแจ้งเตือนเมื่อผู้ใช้ไม่สามารถเข้าถึงฟอร์มได้
      </p>

      <div className="space-y-3">
        {errorTypes.map(({ key, label, description, icon: Icon, color }) => (
          <div key={key} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => setExpandedType(expandedType === key ? null : key)}
              className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <Icon className={`h-5 w-5 ${color}`} />
                <div>
                  <div className="font-medium text-gray-900">{label}</div>
                  <div className="text-sm text-gray-500">{description}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {isCustomized(key) && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    Customized
                  </span>
                )}
                <div className={`transform transition-transform ${expandedType === key ? 'rotate-180' : ''}`}>
                  ▼
                </div>
              </div>
            </button>

            {expandedType === key && (
              <div className="px-4 pb-4 border-t border-gray-200">
                <div className="space-y-3 pt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ข้อความหลัก
                    </label>
                    <input
                      type="text"
                      value={localErrorMessages[key]?.message || defaultMessages[key].message}
                      onChange={(e) => handleMessageChange(key, 'message', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={defaultMessages[key].message}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      รายละเอียด
                    </label>
                    <textarea
                      value={localErrorMessages[key]?.details || defaultMessages[key].details}
                      onChange={(e) => handleMessageChange(key, 'details', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={defaultMessages[key].details}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ใช้ {'{variable}'} สำหรับใส่ค่าตัวแปร เช่น {'{current_register}'}, {'{start_date}'}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => handleResetToDefault(key)}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Reset to Default
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">💡 Tips:</p>
            <ul className="space-y-1">
              <li>• ข้อความจะถูกใช้ตามลำดับ: Flow Settings → Custom Messages → Default Messages</li>
              <li>• ใช้ {'{variable}'} สำหรับใส่ค่าตัวแปรที่ระบบจะแทนที่อัตโนมัติ</li>
              <li>• หากไม่กำหนดข้อความ ระบบจะใช้ข้อความเริ่มต้น</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
